// Copyright (c) 2021 embyt GmbH. All rights reserved.
// Author: Roman Morawek

import * as React from "react";
import { connect } from "react-redux";
import { FormattedMessage, defineMessages, WrappedComponentProps, injectIntl } from "react-intl";
import { NavigateFunction } from "react-router-dom";

import { RoutedProps, withRouter } from "view/reactrouterinterceptor";
import { ActionType, IAction, raiseAction } from "actions";
import { MainPanel } from "types";
import { isSet, round } from "utils";
import { tabClickHandler } from "guiutils";
import { IState, IStateIdList } from "states/states";
import { IConnectorConfig, IConnectorData, IOcppCharger } from "states/ocppstates";
import { ICharger } from "parameter";
import Connector, { ConnectorStatus } from "model/connector";

import SearchFilter from "./SearchFilter";

interface IChargerListExtProps {
  isDashboard: boolean;
  showOnlyCharging?: boolean;
}

interface IChargerListProps {
  selectedCharger: number | undefined; // ocpp charger id
  selectedConnector: number | undefined;
  chargers: IOcppCharger[];
  chargerParameter: ICharger[];
  connectorConfig: (IConnectorConfig | undefined)[][];
  connectorData: (IConnectorData | undefined)[][];
  lastOcppMessage: IStateIdList<number | undefined>;
  lastUpdate: number;
  isRfidUser: boolean;
}

const mapStateToProps = (state: IState) => {
  // used to ensure screen update
  let lastUpdate = state.ocpp.lastOcppMessage.reduce((prev, cur) => Math.max(prev, cur), 0);
  lastUpdate = Math.max(
    lastUpdate,
    (state.scheduler.ocppConnectorData.lastData ?? 0) / 1000,
    (state.scheduler.ocppChargerList.lastData ?? 0) / 1000,
  );

  const props: IChargerListProps = {
    selectedCharger: state.router.charger,
    selectedConnector: state.router.connector,
    chargers: state.ocpp.charger,
    chargerParameter: state.parameter?.charger ?? [],
    connectorConfig: state.ocpp.connectorConfig,
    connectorData: state.ocpp.connectorData,
    lastOcppMessage: state.ocpp.lastOcppMessage,
    lastUpdate,
    isRfidUser: !!state.site.user?.isRfidUser,
  };
  return props;
};

interface IChargerListState {
  filterText: string;
}

interface IChargerListDispatch {
  onSelectCharger: (
    charger: number | undefined,
    connector: number | undefined,
    navigate: NavigateFunction,
  ) => void;
  onSelectChargerForFilter: (chargerName: string | undefined, navigate: NavigateFunction) => void;
}

const mapDispatchToProps = (dispatch: (action: IAction<any>) => void) => {
  const dispatchProps: IChargerListDispatch = {
    onSelectCharger: (
      charger: number | undefined,
      connector: number | undefined,
      navigate: NavigateFunction,
    ) => {
      if (isSet(charger) && isSet(connector)) {
        navigate(`../${MainPanel.CHARGERDETAIL}?charger=${charger}&connector=${connector}`);
      } else if (isSet(charger)) {
        navigate(`../${MainPanel.CHARGERDETAIL}?charger=${charger}`);
      } else {
        navigate(`../${MainPanel.CHARGERDETAIL}`);
      }
    },
    onSelectChargerForFilter: (chargerName: string | undefined, navigate: NavigateFunction) => {
      dispatch(raiseAction(ActionType.ACT_FILTER_CHARGERNAME, chargerName));
      navigate(`../${MainPanel.CHARGINGSESSIONLIST}`);
    },
  };
  return dispatchProps;
};

const messages = defineMessages({
  connector: {
    id: "Connector",
  },
  placeholderSearchField: {
    id: "Search for chargers...",
  },
  toolTipSearchField: {
    id: "Search for charger name, vendor or serial number. To search for multiple fields, separate search texts by a space!",
  },
});

class ChargerListComponent extends React.Component<
  IChargerListExtProps &
    IChargerListProps &
    IChargerListDispatch &
    WrappedComponentProps &
    RoutedProps,
  IChargerListState
> {
  public state: IChargerListState = {
    filterText: "",
  };

  public render() {
    const chargerIndicis = Array.from(Array(this.props.chargers.length).keys()).sort((a, b) => {
      const keyA = this.props.chargers[a]?.name ?? this.props.chargers[a]?.chargepoint;
      const keyB = this.props.chargers[b]?.name ?? this.props.chargers[b]?.chargepoint;
      return isSet(keyA) && isSet(keyB) && keyA > keyB ? 1 : -1;
    });
    const chargerItems: JSX.Element[] = [];
    chargerIndicis.forEach((curIndex) => {
      const curCharger = this.renderCharger(curIndex);
      // curCharger might be undefined if this charger is disabled
      if (curCharger) {
        chargerItems.push(curCharger);
      }
    });
    const className = ["charger", "link", "all-sessions"];
    if (this.props.selectedCharger === undefined) {
      className.push("selected");
    }
    const totalSockets = chargerIndicis.reduce(
      (prev, curr) => prev + (this.props.chargers[curr].numberofconnectors ?? 1),
      0,
    );

    return (
      <>
        {this.props.isDashboard === false && (
          <SearchFilter
            className="tableSearchBoxSidebar"
            filterText={this.state.filterText}
            placeholder={this.props.intl.formatMessage(messages.placeholderSearchField)}
            tooltip={this.props.intl.formatMessage(messages.toolTipSearchField)}
            onChange={(filterText) => {
              this.setState({ filterText });
            }}
          />
        )}
        <div id="chargerlist">
          {chargerItems.length > 0 ? (
            <div id="chargerlistframe">
              <table>
                <tbody>
                  {!this.props.isDashboard && totalSockets > 1 && !this.props.isRfidUser && (
                    <tr
                      key="nocharger"
                      className={className.join(" ")}
                      tabIndex={0}
                      onKeyDown={tabClickHandler}
                      onClick={() =>
                        this.props.onSelectCharger(undefined, undefined, this.props.navigate)
                      }
                    >
                      <td colSpan={3}>
                        <FormattedMessage id="show all charging sessions" />
                      </td>
                    </tr>
                  )}
                  {chargerItems}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="alert alert-warning">
              <FormattedMessage id="No chargers present." />
            </p>
          )}
        </div>
      </>
    );
  }

  private getConnectors(chargerIndex: number, filter: string[] | undefined) {
    const nrConnectors = Math.max(
      this.props.chargers[chargerIndex].numberofconnectors ??
        (this.props.connectorConfig[chargerIndex]?.length ?? 2) - 1,
      0,
    );
    // start connector numbering with 1, even if this the array index
    const connectorIndexes = Array.from(Array(nrConnectors).keys()).map((curIndex) => curIndex + 1);
    if (!isSet(filter)) {
      return { nrConnectors, connectorIndexes };
    }
    return {
      nrConnectors,
      connectorIndexes: connectorIndexes.filter((curIndex) =>
        filter.every((curFilter) =>
          this.props.connectorConfig[chargerIndex]?.[curIndex]?.name
            ?.toLocaleLowerCase()
            .includes(curFilter),
        ),
      ),
    };
  }

  private renderCharger(chargerIndex: number) {
    const filterTextsLowerCase = this.state.filterText.toLocaleLowerCase().split(" ");
    const charger = this.props.chargers[chargerIndex];

    const isChargerShown = filterTextsLowerCase.every(
      (curFilter) =>
        curFilter === "" ||
        charger.name?.toLocaleLowerCase().includes(curFilter) ||
        charger.chargepoint.toLocaleLowerCase().includes(curFilter) ||
        charger.serial?.toLocaleLowerCase().includes(curFilter) ||
        charger.vendor?.toLocaleLowerCase().includes(curFilter),
    );
    const { nrConnectors, connectorIndexes } = this.getConnectors(
      chargerIndex,
      isChargerShown ? undefined : filterTextsLowerCase,
    );
    if (!isChargerShown && connectorIndexes.length === 0) {
      // filter does neither match charger nor any connector
      return undefined;
    }

    const connectorNumber = nrConnectors === 1 ? connectorIndexes[0] : 0;
    const connector = new Connector(
      this.props.connectorData[chargerIndex],
      connectorNumber,
      this.props.lastOcppMessage[chargerIndex],
    );

    const className = ["charger"];
    const hasChargerLink = nrConnectors <= 1 && !this.props.isRfidUser;
    if (hasChargerLink || this.props.isRfidUser) className.push("link");
    className.push(nrConnectors <= 1 ? "no-connectors" : "with-connectors");
    if (
      !this.props.isDashboard &&
      charger.fk_charger === this.props.selectedCharger &&
      nrConnectors === 1
    ) {
      className.push("selected");
    }
    const connectorRows = connectorIndexes.map((curIndex) => {
      return this.renderConnector(chargerIndex, curIndex);
    });

    // render charger, and potentially connector rows
    return (
      <React.Fragment key={charger.fk_charger}>
        <tr
          key={charger.fk_charger}
          className={className.join(" ")}
          tabIndex={hasChargerLink ? 0 : undefined}
          onKeyDown={hasChargerLink ? tabClickHandler : undefined}
          onClick={
            hasChargerLink
              ? () => this.props.onSelectCharger(charger.fk_charger, undefined, this.props.navigate)
              : this.props.isRfidUser
              ? () => this.props.onSelectChargerForFilter(charger.chargepoint, this.props.navigate)
              : undefined
          }
        >
          <td className="status-circle">{nrConnectors <= 1 && this.renderStatusIcon(connector)}</td>
          <td>{charger.name ?? charger.chargepoint}</td>
          <td className="charger-status">
            {connector.isOnline() ? (
              <>
                <FormattedMessage id={`ChargerStatus_${connector.status ?? ""}`} />
                {connector.status === ConnectorStatus.CHARGING && isSet(connector.data?.power) && (
                  <span className="nowrap"> {round(connector.data!.power, 1)} kW</span>
                )}
              </>
            ) : (
              <FormattedMessage id="Offline" />
            )}
          </td>
        </tr>
        {nrConnectors > 1 && connectorRows}
      </React.Fragment>
    );
  }

  private renderConnector(chargerIndex: number, conIndex: number) {
    const charger = this.props.chargers[chargerIndex];
    const connectorConfig = this.props.connectorConfig[chargerIndex]?.[conIndex];
    const connectorData = this.props.connectorData[chargerIndex]?.[conIndex];
    const connectorStatus = connectorData?.status;
    const connector = new Connector(
      this.props.connectorData[chargerIndex],
      conIndex,
      this.props.lastOcppMessage[chargerIndex],
    );

    if (this.props.showOnlyCharging === true && connector.status !== ConnectorStatus.CHARGING) {
      return undefined;
    }

    const hasConnectorLink = !this.props.isRfidUser;
    const className = ["connector"];
    if (hasConnectorLink) className.push("link");
    if (conIndex === 1) {
      className.push("first-connector");
    }
    if (
      !this.props.isDashboard &&
      charger.fk_charger === this.props.selectedCharger &&
      conIndex === this.props.selectedConnector
    ) {
      className.push("selected");
    }

    return (
      <tr
        key={`${chargerIndex}-${conIndex}`}
        className={className.join(" ")}
        tabIndex={hasConnectorLink ? 0 : undefined}
        onKeyDown={hasConnectorLink ? tabClickHandler : undefined}
        onClick={
          hasConnectorLink
            ? () => this.props.onSelectCharger(charger.fk_charger, conIndex, this.props.navigate)
            : undefined
        }
      >
        <td className="status-circle">{this.renderStatusIcon(connector)}</td>
        <td>{this.getConnectorName(conIndex, connectorConfig?.name)}</td>
        <td className="charger-status">
          <FormattedMessage id={`ChargerStatus_${connectorStatus ?? ""}`} />
          {connectorStatus === ConnectorStatus.CHARGING && isSet(connectorData?.power) && (
            <span className="nowrap"> {round(connectorData!.power, 1)} kW</span>
          )}
        </td>
      </tr>
    );
  }

  private renderStatusIcon(charger: Connector) {
    const color = charger.getStatusColor();
    return (
      <div style={{ width: 12, height: 12, borderRadius: "50%", backgroundColor: color }}></div>
    );
  }

  private getConnectorName(conIndex: number, connectorName: string | undefined) {
    if (connectorName) {
      return connectorName;
    }
    // provide default name
    return `${this.props.intl.formatMessage(messages.connector)} ${conIndex}`;
  }
}

export default injectIntl(
  connect(mapStateToProps, mapDispatchToProps)(withRouter(ChargerListComponent)),
);
